import { PropsWithChildren, ReactElement, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

interface PaloSlotValue {
    element: HTMLElement | null;
    setElement: (element: HTMLElement | null) => void;
}

/**
 * A place one step of a tab keeps for another step of the same tab.
 *
 * The stepper draws the steps of a tab one after the other. Where the second
 * belongs somewhere else on the page — the positions between the head and the
 * remarks, the dispatch list above the supplier search — it keeps its own
 * state, form and validation, and only its output is drawn into the place the
 * first one offers (#2208, #2252, #2258).
 */
export const createPaloSlot = () => {
    const Context = createContext<PaloSlotValue>({ element: null, setElement: () => undefined });

    const Provider = ({ children }: PropsWithChildren): ReactElement => {
        const [element, setElement] = useState<HTMLElement | null>(null);
        const value = useMemo(() => ({ element, setElement }), [element]);

        return <Context.Provider value={value}>{children}</Context.Provider>;
    };

    /** The place, offered by the step that holds it. */
    const Slot = (): ReactElement => {
        const { setElement } = useContext(Context);
        // A callback ref rather than an effect: it runs when the node appears
        // and again with null when it goes, which is exactly when the other
        // step has to find it or let go of it.
        const hold = useCallback((element: HTMLDivElement | null) => setElement(element), [setElement]);

        return <div ref={hold} />;
    };

    /**
     * Draws its children into the place. Nothing is drawn until the place has
     * been offered; that is the same render, so the output never stands
     * anywhere else in between.
     */
    const In = ({ children }: PropsWithChildren): ReactElement | null => {
        const { element } = useContext(Context);

        return element ? createPortal(children, element) : null;
    };

    return { Provider, Slot, In };
};

const positions = createPaloSlot();

/** Where the positions of a document are drawn: between its head and its remarks. */
export const PaloPositionsSlotProvider = positions.Provider;
export const PaloPositionsSlot = positions.Slot;
export const PaloInPositionsSlot = positions.In;
